/**
 * pages/admin/AdminProcedimientos.js
 * Panel de administración de procedimientos (RRHH/Admin)
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Badge, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaUpload, FaFileAlt, FaChartBar } from 'react-icons/fa';
import NavbarG from '../../modules/administrador/components/NavbarG';
import { toast } from 'react-toastify';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

// Roles disponibles
const ROLES_DISPONIBLES = [
  { id: 0, nombre: 'Todos' },
  { id: 1, nombre: 'Administración' },
  { id: 2, nombre: 'Inspección' },
  { id: 3, nombre: 'Taller' },
  { id: 4, nombre: 'Conducción' }
];

const AdminProcedimientos = () => {
  const [procedimientos, setProcedimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuario, setUsuario] = useState(null);
  
  // Estados del form
  const [showFormModal, setShowFormModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [procedimientoActual, setProcedimientoActual] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    version: 1,
    min_seconds: 30,
    requiere_scroll_fin: 0,
    requiere_confirmacion: 1,
    activo: 1
  });

  // Estado upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState(''); // Nombre personalizado

  // Estado estadísticas
  const [showEstadisticasModal, setShowEstadisticasModal] = useState(false);
  const [estadisticas, setEstadisticas] = useState(null);
  const [lecturas, setLecturas] = useState([]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUsuario(JSON.parse(storedUser));
    }
    fetchProcedimientos();
  }, []);

  const fetchProcedimientos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/admin/procedimientos`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Error al cargar procedimientos');

      const data = await response.json();
      setProcedimientos(data.data || []);
    } catch (err) {
      toast.error('Error al cargar procedimientos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // CREAR/EDITAR
  const handleShowForm = (procedimiento = null) => {
    if (procedimiento) {
      setModoEdicion(true);
      setProcedimientoActual(procedimiento);
      setFormData({
        titulo: procedimiento.titulo,
        descripcion: procedimiento.descripcion || '',
        version: procedimiento.version,
        min_seconds: procedimiento.min_seconds,
        requiere_scroll_fin: procedimiento.requiere_scroll_fin,
        requiere_confirmacion: procedimiento.requiere_confirmacion,
        activo: procedimiento.activo,
        IdRol: procedimiento.IdRol || 0
      });
    } else {
      setModoEdicion(false);
      setProcedimientoActual(null);
      setFormData({
        titulo: '',
        descripcion: '',
        version: 1,
        min_seconds: 30,
        requiere_scroll_fin: 0,
        requiere_confirmacion: 1,
        activo: 1,
        IdRol: 0
      });
    }
    setShowFormModal(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = modoEdicion 
        ? `${API_URL}/api/admin/procedimientos/${procedimientoActual.id}`
        : `${API_URL}/api/admin/procedimientos`;
      
      const method = modoEdicion ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Error al guardar');

      await response.json();
      
      toast.success(modoEdicion ? 'Procedimiento actualizado' : 'Procedimiento creado');
      setShowFormModal(false);
      fetchProcedimientos();
    } catch (err) {
      toast.error('Error al guardar procedimiento');
      console.error(err);
    }
  };

  // UPLOAD ARCHIVO
  const handleShowUpload = (procedimiento) => {
    setProcedimientoActual(procedimiento);
    setSelectedFile(null);
    setNombreArchivo(procedimiento.titulo || ''); // Pre-llenar con título del procedimiento
    setShowUploadModal(true);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      toast.warning('Seleccioná un archivo');
      return;
    }

    if (!nombreArchivo.trim()) {
      toast.warning('Ingresá un nombre para el archivo');
      return;
    }

    try {
      setUploadingFile(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('archivo', selectedFile);
      formData.append('nombre_personalizado', nombreArchivo.trim());

      const response = await fetch(
        `${API_URL}/api/admin/procedimientos/${procedimientoActual.id}/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) throw new Error('Error al subir archivo');

      toast.success('Archivo subido correctamente');
      setShowUploadModal(false);
      setSelectedFile(null);
      setNombreArchivo('');
      fetchProcedimientos();
    } catch (err) {
      toast.error('Error al subir archivo');
      console.error(err);
    } finally {
      setUploadingFile(false);
    }
  };

  // ESTADÍSTICAS
  const handleShowEstadisticas = async (procedimiento) => {
    try {
      setProcedimientoActual(procedimiento);
      setShowEstadisticasModal(true);
      
      const token = localStorage.getItem('token');
      
      // Obtener estadísticas
      const statsResponse = await fetch(
        `${API_URL}/api/admin/procedimientos/${procedimiento.id}/estadisticas`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setEstadisticas(statsData.data);
      }
      
      // Obtener lecturas
      const lecturasResponse = await fetch(
        `${API_URL}/api/admin/procedimientos/${procedimiento.id}/lecturas`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (lecturasResponse.ok) {
        const lecturasData = await lecturasResponse.json();
        setLecturas(lecturasData.data || []);
      }
    } catch (err) {
      toast.error('Error al cargar estadísticas');
      console.error(err);
    }
  };

  // ELIMINAR (baja lógica)
  const handleEliminar = async (procedimiento) => {
    if (!window.confirm(`¿Desactivar el procedimiento "${procedimiento.titulo}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${API_URL}/api/admin/procedimientos/${procedimiento.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Procedimiento desactivado');
      fetchProcedimientos();
    } catch (err) {
      toast.error('Error al desactivar procedimiento');
      console.error(err);
    }
  };

  const getRolNombre = (idRol) => {
    const rol = ROLES_DISPONIBLES.find(r => r.id === idRol);
    return rol ? rol.nombre : 'Sin asignar';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando...</p>
      </div>
    );
  }

  return (
    <>
      <NavbarG userData={usuario} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2><FaFileAlt /> Administración de Procedimientos</h2>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => handleShowForm()}>
              <FaPlus /> Nuevo Procedimiento
            </Button>
          </Col>
        </Row>

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Título</th>
                  <th>Versión</th>
                  <th>Archivo</th>
                  <th>Roles</th>
                  <th>Config</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {procedimientos.map(proc => (
                  <tr key={proc.id}>
                    <td>{proc.id}</td>
                    <td>
                      <strong>{proc.titulo}</strong>
                      {proc.descripcion && (
                        <><br /><small className="text-muted">{proc.descripcion}</small></>
                      )}
                    </td>
                    <td>
                      <Badge bg="secondary">v{proc.version}</Badge>
                    </td>
                    <td>
                      {proc.archivo_path ? (
                        <small className="text-success">✓ {proc.archivo_nombre}</small>
                      ) : (
                        <small className="text-danger">Sin archivo</small>
                      )}
                    </td>
                    <td>
                      <Badge bg="info">{getRolNombre(proc.IdRol)}</Badge>
                    </td>
                    <td>
                      <small>
                        ⏱️ {proc.min_seconds}s
                        {proc.requiere_scroll_fin === 1 && ' | 📜 Scroll'}
                      </small>
                    </td>
                    <td>
                      <Badge bg={proc.activo === 1 ? 'success' : 'danger'}>
                        {proc.activo === 1 ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1 mb-1"
                        onClick={() => handleShowForm(proc)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-1 mb-1"
                        onClick={() => handleShowUpload(proc)}
                      >
                        <FaUpload />
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="me-1 mb-1"
                        onClick={() => handleShowEstadisticas(proc)}
                      >
                        <FaChartBar />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="mb-1"
                        onClick={() => handleEliminar(proc)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* MODAL CREAR/EDITAR */}
        <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {modoEdicion ? 'Editar Procedimiento' : 'Nuevo Procedimiento'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmitForm}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Título *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.titulo}
                  onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.descripcion}
                  onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Versión</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={formData.version}
                      onChange={(e) => setFormData({...formData, version: parseInt(e.target.value)})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tiempo mínimo (segundos)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={formData.min_seconds}
                      onChange={(e) => setFormData({...formData, min_seconds: parseInt(e.target.value)})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Requiere scroll completo"
                  checked={formData.requiere_scroll_fin === 1}
                  onChange={(e) => setFormData({...formData, requiere_scroll_fin: e.target.checked ? 1 : 0})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Requiere confirmación"
                  checked={formData.requiere_confirmacion === 1}
                  onChange={(e) => setFormData({...formData, requiere_confirmacion: e.target.checked ? 1 : 0})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Activo"
                  checked={formData.activo === 1}
                  onChange={(e) => setFormData({...formData, activo: e.target.checked ? 1 : 0})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Asignar a Rol *</Form.Label>
                <Form.Select
                  value={formData.IdRol}
                  onChange={(e) => setFormData({...formData, IdRol: parseInt(e.target.value)})}
                  required
                >
                  {ROLES_DISPONIBLES.map(rol => (
                    <option key={rol.id} value={rol.id}>
                      {rol.nombre}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Seleccionar "Todos" para que sea visible por todos los roles
                </Form.Text>
              </Form.Group>

              <hr />


            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowFormModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit">
                {modoEdicion ? 'Actualizar' : 'Crear'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* MODAL UPLOAD */}
        <Modal show={showUploadModal} onHide={() => {
          setShowUploadModal(false);
          setSelectedFile(null);
          setNombreArchivo('');
        }}>
          <Modal.Header closeButton>
            <Modal.Title>Subir Archivo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {procedimientoActual && (
              <>
                <p><strong>{procedimientoActual.titulo}</strong></p>
                <Alert variant="info" className="small mb-3">
                  📄 <strong>Formatos permitidos:</strong> PDF, PNG, JPG, JPEG, GIF, WEBP, BMP, SVG<br/>
                  📏 <strong>Tamaño máximo:</strong> 50 MB
                </Alert>
                
                <Form.Group className="mb-3">
                  <Form.Label>Nombre del archivo *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: Manual de Seguridad"
                    value={nombreArchivo}
                    onChange={(e) => setNombreArchivo(e.target.value)}
                    maxLength={100}
                  />
                  <Form.Text className="text-muted">
                    Este será el nombre visible para los usuarios
                  </Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Seleccionar archivo *</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
                    onChange={handleFileChange}
                  />
                </Form.Group>
                {selectedFile && (
                  <Alert variant="info" className="mt-3">
                    📎 Archivo seleccionado: {selectedFile.name}
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowUploadModal(false);
              setSelectedFile(null);
              setNombreArchivo('');
            }}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleUploadFile}
              disabled={!selectedFile || !nombreArchivo.trim() || uploadingFile}
            >
              {uploadingFile ? <Spinner animation="border" size="sm" /> : <FaUpload />} Subir
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL ESTADÍSTICAS */}
        <Modal show={showEstadisticasModal} onHide={() => setShowEstadisticasModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Estadísticas y Lecturas</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {procedimientoActual && (
              <>
                <h5>{procedimientoActual.titulo}</h5>
                
                {estadisticas && (
                  <Card className="mb-3">
                    <Card.Body>
                      <Row>
                        <Col md={4} className="text-center">
                          <h3>{estadisticas.total_asignados}</h3>
                          <small className="text-muted">Total Asignados</small>
                        </Col>
                        <Col md={4} className="text-center">
                          <h3 className="text-success">{estadisticas.confirmados}</h3>
                          <small className="text-muted">Confirmados</small>
                        </Col>
                        <Col md={4} className="text-center">
                          <h3 className="text-warning">{estadisticas.pendientes}</h3>
                          <small className="text-muted">Pendientes</small>
                        </Col>
                      </Row>
                      <ProgressBar className="mt-3">
                        <ProgressBar
                          variant="success"
                          now={(estadisticas.confirmados / estadisticas.total_asignados) * 100}
                          label={`${Math.round((estadisticas.confirmados / estadisticas.total_asignados) * 100)}%`}
                        />
                      </ProgressBar>
                    </Card.Body>
                  </Card>
                )}

                <h6>Lecturas Registradas</h6>
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Empleado</th>
                      <th>Apertura</th>
                      <th>Descarga</th>
                      <th>Scroll</th>
                      <th>Confirmado</th>
                      <th>Tiempo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lecturas.map((lectura, idx) => (
                      <tr key={idx}>
                        <td>
                          {lectura.NmEmpleado}
                          <br />
                          <small className="text-muted">Leg. {lectura.legajo || 'N/A'}</small>
                        </td>
                        <td>
                          {lectura.opened_at ? (
                            <small>✓ {new Date(lectura.opened_at).toLocaleString('es-AR')}</small>
                          ) : '-'}
                        </td>
                        <td>
                          {lectura.downloaded_at ? <small>✓</small> : '-'}
                        </td>
                        <td>
                          {lectura.scroll_completed_at ? <small>✓</small> : '-'}
                        </td>
                        <td>
                          {lectura.confirmed_at ? (
                            <Badge bg="success">
                              {new Date(lectura.confirmed_at).toLocaleDateString('es-AR')}
                            </Badge>
                          ) : (
                            <Badge bg="warning">Pendiente</Badge>
                          )}
                        </td>
                        <td>
                          {lectura.segundos_lectura ? `${lectura.segundos_lectura}s` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEstadisticasModal(false)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default AdminProcedimientos;
